import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Pill, Plus, Search, Edit, Loader2, Download, Upload, FileText,
  CheckCircle, AlertCircle, XCircle, Activity, Eye
} from 'lucide-react';
import { API_URL } from './constants';
import { MedicationDetailPanel } from './MedicationDetailPanel';
import { MedicationFormPanel } from './MedicationFormPanel';

export const MedicationCatalogTab = () => {
  const [medications, setMedications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFormPanel, setShowFormPanel] = useState(false);
  const [editingMedication, setEditingMedication] = useState(null);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  
  // Import/Export state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMedications();
  }, []);

  const fetchMedications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/medications/catalog`, {
        params: { search: searchTerm || undefined }
      });
      setMedications(response.data);
    } catch (error) {
      console.error('Error fetching medications:', error);
    }
  };

  const handleAddMedication = async (formData) => {
    const params = {
      ...formData,
      alternative_names: formData.alternative_names?.length > 0 ? JSON.stringify(formData.alternative_names) : undefined
    };
    await axios.post(`${API_URL}/api/medications/catalog`, null, { params });
    toast.success('Medicamento agregado al catálogo');
    fetchMedications();
  };

  const handleEditMedication = (med) => {
    setEditingMedication({
      id: med.id,
      generic_name: med.generic_name || '',
      commercial_name: med.commercial_name || '',
      active_ingredient: med.active_ingredient || '',
      concentration: med.concentration || '',
      form: med.form || '',
      manufacturer: med.manufacturer || '',
      is_controlled: med.is_controlled || false,
      requires_refrigeration: med.requires_refrigeration || false,
      alternative_names: med.alternative_names || []
    });
    setShowFormPanel(true);
  };

  const handleUpdateMedication = async (formData) => {
    if (!editingMedication?.id) return;
    
    await axios.put(`${API_URL}/api/medications/catalog/${editingMedication.id}`, null, {
      params: {
        generic_name: formData.generic_name,
        commercial_name: formData.commercial_name,
        active_ingredient: formData.active_ingredient,
        concentration: formData.concentration,
        form: formData.form,
        manufacturer: formData.manufacturer,
        is_controlled: formData.is_controlled,
        requires_refrigeration: formData.requires_refrigeration,
        alternative_names: JSON.stringify(formData.alternative_names || [])
      }
    });
    toast.success('Medicamento actualizado');
    setEditingMedication(null);
    fetchMedications();
  };

  const closeFormPanel = () => {
    setShowFormPanel(false);
    setEditingMedication(null);
  };

  const openAddForm = () => {
    setEditingMedication(null);
    setShowFormPanel(true);
  };

  // Import/Export handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/medications/catalog/export/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'medications_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Plantilla descargada');
    } catch (error) {
      toast.error('Error descargando plantilla');
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/medications/catalog/export/data`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `medications_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${medications.length} medicamentos exportados`);
    } catch (error) {
      toast.error('Error exportando medicamentos');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Solo se permiten archivos CSV');
        return;
      }
      setImportFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    setIsImporting(true);
    setImportResults(null);
    
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(`${API_URL}/api/medications/catalog/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResults(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} medicamentos importados`);
        fetchMedications();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error importando medicamentos');
    } finally {
      setIsImporting(false);
    }
  };

  const closeImportDialog = () => {
    setIsImportOpen(false);
    setImportFile(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Buscar medicamento..."
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchMedications()}
            data-testid="medication-search-input"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadTemplate}
          data-testid="download-template-btn"
        >
          <FileText className="h-4 w-4 mr-2" />
          Plantilla
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={isExporting || medications.length === 0}
          data-testid="export-btn"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Exportar
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsImportOpen(true)}
          data-testid="import-btn"
        >
          <Upload className="h-4 w-4 mr-2" />
          Importar
        </Button>
        <Button onClick={openAddForm} data-testid="add-medication-btn">
          <Plus className="h-4 w-4 mr-2" />
          Agregar
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {medications.map(med => (
          <Card key={med.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-[#FDE8EA] flex items-center justify-center">
                    <Pill className="h-5 w-5 text-[#E63946]" />
                  </div>
                  <div>
                    <p className="font-medium">{med.generic_name}</p>
                    <p className="text-sm text-gray-500">{med.commercial_name || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {med.is_controlled && (
                    <Badge variant="destructive" className="text-xs">Controlado</Badge>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      setSelectedMedication(med);
                      setShowDetailPanel(true);
                    }}
                    className="h-8 w-8 p-0"
                    data-testid={`view-medication-${med.id}`}
                  >
                    <Eye className="h-4 w-4 text-gray-500 hover:text-[#E63946]" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleEditMedication(med)}
                    className="h-8 w-8 p-0"
                    data-testid={`edit-medication-${med.id}`}
                  >
                    <Edit className="h-4 w-4 text-gray-500 hover:text-[#E63946]" />
                  </Button>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                <p><span className="text-gray-500">Principio activo:</span> {med.active_ingredient}</p>
                <p><span className="text-gray-500">Concentración:</span> {med.concentration}</p>
                <p><span className="text-gray-500">Forma:</span> {med.form}</p>
                {med.manufacturer && (
                  <p><span className="text-gray-500">Fabricante:</span> {med.manufacturer}</p>
                )}
              </div>
              {med.requires_refrigeration && (
                <Badge variant="outline" className="mt-2 text-xs border-blue-500 text-blue-600">
                  Requiere refrigeración
                </Badge>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {medications.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Pill className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>No hay medicamentos en el catálogo</p>
          <Button variant="outline" className="mt-4" onClick={openAddForm}>
            <Plus className="h-4 w-4 mr-2" />
            Agregar primer medicamento
          </Button>
        </div>
      )}

      {/* Medication Form Panel */}
      <MedicationFormPanel
        medication={editingMedication}
        isOpen={showFormPanel}
        onClose={closeFormPanel}
        onSubmit={editingMedication?.id ? handleUpdateMedication : handleAddMedication}
      />

      {/* Import Dialog */}
      <Dialog open={isImportOpen} onOpenChange={closeImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#E63946]" />
              Importar Medicamentos
            </DialogTitle>
            <DialogDescription>
              Sube un archivo CSV para importar medicamentos al catálogo
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Input */}
            <div>
              <Label>Seleccionar archivo CSV</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="mt-1"
              />
              {importFile && (
                <p className="text-sm text-muted-foreground mt-1">
                  {importFile.name}
                </p>
              )}
            </div>

            {/* Import Results */}
            {importResults && (
              <div className="border rounded-lg p-4 space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Resultados de Importación
                </h4>
                
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded p-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-600">{importResults.imported}</p>
                    <p className="text-xs text-muted-foreground">Importados</p>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded p-2">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-yellow-600">{importResults.skipped}</p>
                    <p className="text-xs text-muted-foreground">Omitidos</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded p-2">
                    <XCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-red-600">{importResults.errors?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Errores</p>
                  </div>
                </div>

                {/* Error Details */}
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="max-h-32 overflow-y-auto text-sm">
                    {importResults.errors.map((err, idx) => (
                      <p key={`error-${idx}`} className="text-red-600 text-xs py-1 border-b last:border-0">
                        Fila {err.row}: {err.error}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeImportDialog}>
              Cerrar
            </Button>
            <Button 
              onClick={handleImport} 
              disabled={!importFile || isImporting}
              className="bg-[#E63946] hover:bg-[#d32836]"
            >
              {isImporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medication Detail Panel */}
      <MedicationDetailPanel
        medication={selectedMedication}
        isOpen={showDetailPanel}
        onClose={() => {
          setShowDetailPanel(false);
          setSelectedMedication(null);
        }}
        onUpdate={fetchMedications}
      />
    </div>
  );
};

export default MedicationCatalogTab;
